<script>
  import OutlinedInput from '@/components/form/OutlinedInput.vue';
  import PasswordInput from '@/components/form/PasswordInput.vue';
  import rules from '@/helpers/validation-rules';
  import { SIGN_UP_STEPS, AUTH_PROVIDERS } from '@/helpers/enums';
  import mailcheck from 'mailcheck';
  import { debounce } from 'lodash';
  import IconGoogle from '@/assets/images/logos/005-google.svg';
  import EventBus from '@/plugins/eventBus';
  import { mapGetters, mapState } from 'vuex';
  import auth from '@/mixins/auth';

  export default {
    name: 'SignUpCredentials',

    components: {
      OutlinedInput,
      PasswordInput,
      IconGoogle,
    },

    mixins: [auth],

    emits: ['change'],

    props: {
      model: {
        required: true,
      },
    },

    data: () => ({
      rules,
      AUTH_PROVIDERS,

      emailSuggestion: '',

      usernameAvailable: null,
      emailAvailable: null,

      isUsernameValid: false,
      isEmailValid: false,

      loadings: {
        username: false,
        email: false,
      },

      socialIsPending: false,
    }),

    computed: {
      ...mapState(['loginError', 'user']),
      ...mapGetters(['isUserSuspended', 'isUserBanned']),

      usernameInputRules() {
        return [rules.required(), rules.username(), rules.maxLength(16), rules.minLength(4)];
      },
      usernameRule() {
        return () =>
          this.usernameAvailable === true || this.usernameAvailable === null || 'This username is already taken';
      },

      emailInputRules() {
        return [rules.required(), rules.email(), rules.maxLength(255)];
      },
      emailRule() {
        return () => this.emailAvailable === true || this.emailAvailable === null || 'This email is already taken';
      },
    },

    watch: {
      'model.username': {
        immediate: true,
        handler(value) {
          const messages = this.usernameInputRules.map((rule) => rule(value)).filter((message) => message !== true);

          this.isUsernameValid = messages.length === 0;
        },
      },
      'model.email': {
        handler(value) {
          const currentDomain = value.split('@')[1];
          const previousDomain = this.emailSuggestion.split('@')[1];

          if (currentDomain !== previousDomain || !value) {
            this.emailSuggestion = '';
          }

          if (value === this.emailSuggestion || !value) {
            this.emailSuggestion = '';
          }

          const messages = this.emailInputRules.map((rule) => rule(value)).filter((message) => message !== true);

          this.isEmailValid = messages.length === 0;
        },
        immediate: true,
      },
      usernameAvailable() {
        this.$refs.username?.$refs?.input?.validate();
      },
      emailAvailable() {
        this.$refs.email?.$refs?.input?.validate();
      },
    },

    methods: {
      checkIfUsernameAvailable: debounce(async function (username) {
        if (!this.isUsernameValid) {
          this.usernameAvailable = null;
          return;
        }

        this.loadings.username = true;

        try {
          const { data } = await this.$store.dispatch('checkUsernameAvailability', username);
          this.usernameAvailable = !data.hasError;
        } catch (e) {
          console.error(e);
        }

        this.loadings.username = false;
      }, 300),
      checkIfEmailAvailable: debounce(async function (email) {
        if (!this.isEmailValid) {
          this.emailAvailable = null;
          return;
        }

        this.loadings.email = true;

        try {
          const { data } = await this.$store.dispatch('checkEmailAvailability', email);
          this.emailAvailable = !data.hasError;
        } catch (e) {
          console.error(e);
        }

        this.loadings.email = false;
      }, 300),

      checkForEmailSuggestion() {
        // Docs: https://github.com/mailcheck/mailcheck?tab=readme-ov-file
        mailcheck.defaultDomains.splice(mailcheck.defaultDomains.indexOf('ymail.com'), 1);
        mailcheck.defaultSecondLevelDomains.push('msn', 'yahoo', 'outlook');
        mailcheck.defaultTopLevelDomains.push('io');

        mailcheck.run({
          email: this.model.email,
          suggested: async (suggestion) => {
            this.$set(this, 'emailSuggestion', suggestion.full);
          },
        });
      },

      setSuggestionForEmail() {
        this.model.email = this.emailSuggestion;
        this.emailSuggestion = '';
        this.checkIfEmailAvailable(this.model.email);
      },

      openLogin() {
        this.$store.dispatch('changeSignupDialogState', false);

        setTimeout(() => {
          this.$store.dispatch('changeLoginDialogState', true);
        }, 100);
      },

      submit() {
        if (this.$refs.form.validate() && !this.loadings.username && !this.loadings.email) {
          this.$store.dispatch('changeSignupStep', SIGN_UP_STEPS.PERSONAL_INFO);
        }
      },

      async login(user) {
        const res = await this.$store.dispatch('signUserIn', { ...user, isSocialSignIn: true });

        this.$store.commit('SET_SOCIAL_SIGN_UP', false);

        if (['banned', 'suspended'].includes(res?.error) || !this.user || this.isUserBanned) {
          if (this.$route.path === '/') return false;
          return this.$router.push('/');
        }

        if (this.isUserSuspended) {
          EventBus.emit('user-suspended');
          return;
        }

        if (this.$route.path !== '/lobby') {
          return this.$router.push('/lobby');
        }
      },

      async socialSignIn(provider) {
        this.socialIsPending = true;

        try {
          this.$store.commit('SET_SOCIAL_SIGN_UP', true);

          const result = await this.$store.dispatch('socialSignin', provider);

          if (result?.status === 'success' && result.user) {
            if (result.userExists) {
              await this.login(result.user);

              if (result.user.phoneNumber?.verified) {
                await this.$store.dispatch('changeSignupDialogState', false);
                await this.$store.dispatch('changeSignupStep', SIGN_UP_STEPS.CREDENTIALS);
              } else {
                await this.$store.dispatch('changeSignupStep', SIGN_UP_STEPS.VERIFY_PHONE);
              }
            } else {
              this.$emit('change', result.user);
              await this.$store.dispatch('changeSignupStep', SIGN_UP_STEPS.PERSONAL_INFO);
            }
          } else {
            throw new Error(result.reason);
          }
        } catch (error) {
          this.$store.commit('SET_SOCIAL_SIGN_UP', false);
          await this.$store.dispatch('signOut');
          console.error(`${provider} sign-in error:`, error);
        } finally {
          this.socialIsPending = false;
        }
      },
    },
  };
</script>

<template>
  <v-form ref="form" class="d-flex flex-column mt-6 sign-up-credentials" lazy-validation>
    <button :disabled="socialIsPending" class="social-button" @click="socialSignIn(AUTH_PROVIDERS.GOOGLE)">
      <IconGoogle class="icon" />
      Sign Up with Google
    </button>
    <div class="sign-in-email-text">
      <span>or sign up with email</span>
    </div>

    <outlined-input
      ref="username"
      v-model="model.username"
      :rules="[...usernameInputRules, usernameRule]"
      data-test-id="username-input"
      label="Username"
      @input="checkIfUsernameAvailable(model.username)"
    >
      <template #append>
        <div v-if="isUsernameValid">
          <v-progress-circular v-if="loadings.username" color="white" indeterminate size="25" width="2" />
          <v-icon v-else-if="usernameAvailable" class="green--text">mdi-check</v-icon>
        </div>
      </template>
    </outlined-input>
    <outlined-input
      ref="email"
      v-model="lowerEmail"
      :hint="emailSuggestion"
      :rules="[...emailInputRules, emailRule]"
      data-test-id="email-input"
      label="Email"
      persistent-hint
      @blur="checkForEmailSuggestion"
      @input="checkIfEmailAvailable(model.email)"
    >
      <template #message="{ message }">
        <div v-if="emailSuggestion && model.email" class="gold--text line-height-05">
          <span>Did you mean</span>
          <span class="suggestion ml-1" @click="setSuggestionForEmail">{{ emailSuggestion }}</span>
          <span>?</span>
        </div>
        <span v-else>{{ message }}</span>
      </template>
      <template #append>
        <div v-if="isEmailValid">
          <v-progress-circular v-if="loadings.email" color="white" indeterminate size="25" width="2" />
          <v-icon v-else-if="emailAvailable" class="green--text">mdi-check</v-icon>
        </div>
      </template>
    </outlined-input>

    <PasswordInput v-model="model.password" />

    <v-btn class="golden-button mt-5" data-test-id="next-step" @click="submit">
      <span>Next Step</span>
      <v-icon class="mt-1 font-size-30">mdi-arrow-right-thin</v-icon>
    </v-btn>

    <p class="white--text w-full text-center mt-4 sign-in-text">
      Already have an account?
      <span class="text-underline" data-test-id="login-here" @click="openLogin">Login here</span>
    </p>
  </v-form>
</template>

<style lang="scss" scoped>
  .suggestion {
    text-decoration: underline;
    cursor: pointer;
  }

  .sign-up-credentials {
    gap: 16px;

    .social-button {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 44px;
      border-radius: 10px;
      background: #fff;
      gap: 10px;
      color: #160526;
      font-size: 18px;
      font-weight: 600;
      line-height: 16px;

      &:hover {
        filter: brightness(105%);
      }

      &:active {
        filter: brightness(97%);
      }

      &:disabled {
        pointer-events: none;
        filter: grayscale(1);
      }

      svg {
        max-width: 24px;
        max-height: 24px;
      }
    }

    .sign-in-email-text {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      margin-top: 8px;

      span {
        flex-basis: 100%;
        color: #fff;
        text-align: center;
        font-size: 14px;
        font-weight: 400;
        line-height: 12px;
        opacity: 0.7;
      }

      &:before,
      &:after {
        content: '';
        display: inline-block;
        flex-basis: 100%;
        height: 1px;
        background: #fff;
        opacity: 0.3;
      }
    }
  }

  .sign-in-text {
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
  }

  .text-underline {
    color: var(--v-gold-base);
    text-decoration: underline;
    cursor: pointer;
  }
</style>
